import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import apiService from '../services/api.service';
import CourseCountdown from '../Components/CourseComponents/Countdown/CourseCountdown';
import AttendanceManager from '../Components/CourseComponents/AttendanceManager/AttendanceManager';
import HomePollPanel from '../Components/HomePollPanel/HomePollPanel';
import EventChat from '../Components/EventChat/EventChat';
import { appPath } from '../utils/appSlug';
import { DEFAULT_APP_SLUG } from '../config';
import { toYouTubeEmbedSrc } from '../utils/urls';
import { parseApiDate } from '../utils/datetime';
import './course.css';

const STATUS_CHECK_DELAY = 5000;
const participateStorageKey = (eventId) => `course-participate-${eventId}`;

const Course = ({ loginStatus, appSlug }) => {
    const { id } = useParams();
    const history = useHistory();
    const slug = appSlug || DEFAULT_APP_SLUG;
    const isLoggedIn = Boolean(loginStatus?.sub);
    const participating = sessionStorage.getItem(participateStorageKey(id)) === 'true';

    const [eventDate, setEventDate] = useState();
    const [eventName, setEventName] = useState("");
    const [eventDescription, setEventDescription] = useState("");
    const [embedLink, setEmbedLink] = useState("");
    const [eventStatus, setEventStatus] = useState("");
    const [eventFinished, setEventFinished] = useState(false);

    const getEvent = useCallback(async () => {
        try {
            const response = await apiService.getEvent(id);
            const event = response?.data;
            if (!event) return;
            if (event.embed_link) setEmbedLink(event.embed_link);
            setEventDescription(event.description || "");
            setEventName(event.name || "");
            setEventDate(parseApiDate(event.date)?.getTime());
            setEventFinished(Boolean(event.finished));
        } catch {
            localStorage.setItem('authError', "true");
            history.push(appPath(slug));
        }
    }, [id, history, slug]);

    const getEventStatus = useCallback(async () => {
        try {
            const response = await apiService.getEventStatus(id);
            if (response.data) {
                const status = typeof response.data === 'string'
                    ? response.data
                    : response.data.status;
                setEventStatus(status);
            }
        } catch {
            localStorage.setItem('authError', "true");
            history.push(appPath(slug));
        }
    }, [id, history, slug]);

    useEffect(() => {
        if (!isLoggedIn || !participating) return undefined;
        getEvent();
        const interval = setInterval(getEvent, STATUS_CHECK_DELAY);
        return () => clearInterval(interval);
    }, [getEvent, isLoggedIn, participating]);

    useEffect(() => {
        if (!isLoggedIn || !participating) return undefined;
        getEventStatus();
        const interval = setInterval(getEventStatus, STATUS_CHECK_DELAY);
        return () => clearInterval(interval);
    }, [getEventStatus, isLoggedIn, participating]);

    useEffect(() => {
        if (eventStatus !== "active" || !participating || embedLink) return;
        apiService.getEventEmbed(id).then((response) => {
            if (response.data) setEmbedLink(response.data);
        }).catch(() => {});
    }, [eventStatus, embedLink, id, participating]);

    if (!isLoggedIn) {
        return <Redirect to={appPath(slug, "login")} />;
    }

    if (!participating) {
        return <Redirect to={appPath(slug)} />;
    }

    const isLive = eventStatus === "active" && Boolean(embedLink);

    return (
        <div className="course-page">
            <div className="course-live-row">
                <div className="course-player-wrap">
                    <div className="course-player" aria-label="Player diretta">
                        {eventFinished ? (
                            <div className="course-player__placeholder">L&apos;evento è terminato</div>
                        ) : isLive ? (
                            <div className="course-player__embed">
                                <iframe
                                    src={toYouTubeEmbedSrc(embedLink)}
                                    title="Diretta webinar"
                                    frameBorder="0"
                                    allow="autoplay; encrypted-media"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    sandbox="allow-scripts allow-same-origin allow-presentation"
                                />
                                <div className="course-player__shield" aria-hidden="true" />
                            </div>
                        ) : eventStatus !== "active" && eventDate ? (
                            <div className="course-player__placeholder course-player__placeholder--countdown">
                                <CourseCountdown eventDate={eventDate} />
                            </div>
                        ) : (
                            <div className="course-player__placeholder">
                                Non è presente alcuna diretta
                            </div>
                        )}
                    </div>
                    {eventStatus === "active" && !eventFinished && (
                        <AttendanceManager eventId={id} eventStatus={eventStatus} />
                    )}
                </div>
                <div className="course-side-panels">
                    <HomePollPanel eventId={id} isLoggedIn />
                    {isLive && !eventFinished && <EventChat eventId={id} />}
                </div>
            </div>

            {eventName && (
                <div className="course-info">
                    <h1 className="course-info__title">{eventName}</h1>
                    {eventDescription && <p className="course-info__description">{eventDescription}</p>}
                </div>
            )}
        </div>
    );
};

export default Course;
